import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProductList } from '@harbor/shared';
import { useApi, useDebounced } from '../lib/hooks.js';
import { toQueryString } from '../lib/api.js';
import { Filters, type Facets, type FilterState } from '../components/Filters.js';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard.js';
import { Alert, Badge, Button, EmptyState, Skeleton, TextInput } from '../components/ui.js';

/**
 * Catalogue.
 *
 * ## Filter state lives in the URL
 *
 * `useSearchParams` is the single source of truth. Component state would be
 * simpler to write and would break the back button, sharing, and refresh — three
 * things a customer takes for granted in a shop.
 *
 * ## Pagination is "load more", not numbered pages
 *
 * The API returns a keyset cursor, so there is no page 2 to jump to without
 * walking the cursor chain. A "load more" button matches what the API can
 * actually do, and it avoids the classic offset-pagination bug where scrolling
 * past page 20 starts repeating items.
 */
export function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawQuery = searchParams.get('q') ?? '';
  const debouncedQuery = useDebounced(rawQuery, 320);

  const filters = useMemo<FilterState>(
    () => ({
      ...(searchParams.get('category') ? { category: searchParams.get('category') as string } : {}),
      ...(searchParams.get('minPrice') ? { minPrice: Number(searchParams.get('minPrice')) } : {}),
      ...(searchParams.get('maxPrice') ? { maxPrice: Number(searchParams.get('maxPrice')) } : {}),
      ...(searchParams.get('inStock') === 'true' ? { inStock: true } : {}),
      ...(searchParams.get('sort') ? { sort: searchParams.get('sort') as string } : {}),
    }),
    [searchParams],
  );

  const queryString = toQueryString({
    q: debouncedQuery || undefined,
    category: filters.category,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    inStock: filters.inStock,
    sort: filters.sort,
    limit: 24,
  });

  const { data, error, loading, refetch } = useApi<ProductList>(`/api/products${queryString}`);
  const facets = useApi<Facets>('/api/catalog/facets');

  const updateFilters = useCallback(
    (next: FilterState) => {
      const params = new URLSearchParams(searchParams);
      const set = (key: string, value: string | undefined) => {
        if (value === undefined || value === '') params.delete(key);
        else params.set(key, value);
      };
      set('category', next.category);
      set('minPrice', next.minPrice !== undefined ? String(next.minPrice) : undefined);
      set('maxPrice', next.maxPrice !== undefined ? String(next.maxPrice) : undefined);
      set('inStock', next.inStock ? 'true' : undefined);
      set('sort', next.sort);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const updateQuery = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) params.set('q', value);
      else params.delete('q');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const hasFilters =
    filters.category !== undefined ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.inStock === true ||
    rawQuery !== '';

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Hero />

      <div className="mt-12 grid gap-10 lg:grid-cols-[16rem_1fr] lg:gap-12">
        {/* Filters collapse below the grid on narrow screens; a 16 rem sidebar
            above the products would push them entirely off the first viewport. */}
        <aside className="order-2 lg:order-1">
          <div className="lg:sticky lg:top-24">
            <Filters
              facets={facets.data}
              active={filters}
              onChange={updateFilters}
              resultCount={data?.total ?? 0}
            />
          </div>
        </aside>

        <section className="order-1 lg:order-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full max-w-sm">
              <SearchIcon />
              <TextInput
                type="search"
                value={rawQuery}
                onChange={(event) => updateQuery(event.target.value)}
                placeholder="Search the catalogue"
                aria-label="Search products"
                className="pl-9"
              />
            </div>
            {data && (
              <p className="tabular text-sm text-muted">
                {data.items.length} of {data.total} shown
              </p>
            )}
          </div>

          {error && (
            <div className="mt-6">
              <Alert tone="danger" title="Could not load the catalogue">
                <p>{error.message}</p>
                <Button variant="secondary" size="sm" className="mt-3" onClick={refetch}>
                  Try again
                </Button>
              </Alert>
            </div>
          )}

          {loading && !data ? (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          ) : data && data.items.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                title="Nothing matches those filters"
                description={
                  hasFilters
                    ? 'Try widening the price range or clearing a filter. The catalogue is small by design.'
                    : 'The catalogue is empty. Run the seed script to populate it.'
                }
                action={
                  hasFilters ? (
                    <Button variant="secondary" size="sm" onClick={() => setSearchParams({})}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              <div
                className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
                /* `aria-busy` tells assistive technology the region is updating,
                   so a filter change does not read as a layout change with no
                   explanation. */
                aria-busy={loading}
              >
                {data?.items.map((product, index) => (
                  <ProductCard key={product.id} product={product} priority={index < 3} />
                ))}
              </div>

              {loading && (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 3 }, (_, index) => (
                    <ProductCardSkeleton key={index} />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The hero is a plain marketing block with a CSS grid texture rather than a
 * photograph. A stock hero image is the single most recognisable signal that a
 * project is a template, and it also costs 200 KB on the critical path.
 */
function Hero() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-line bg-surface">
      <div className="grid-texture absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative px-6 py-14 sm:px-10 sm:py-20 lg:px-14">
        <Badge tone="accent">Runs entirely on Cloudflare's edge</Badge>
        <h1 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          Furniture for people who work at desks, not people who photograph them.
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted">
          Fourteen products, chosen because they are repairable. Prices include the cost of the
          parts you will eventually need, which is why nothing here is the cheapest option.
        </p>

        <dl className="mt-10 grid max-w-2xl grid-cols-2 gap-6 sm:grid-cols-4">
          <Metric value="14" label="Products" />
          <Metric value="5" label="Categories" />
          <Metric value="10 yr" label="Spares window" />
          <Metric value="0" label="Disposable parts" />
        </dl>
      </div>
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-subtle">{label}</dt>
      <dd className="tabular mt-1 text-xl font-semibold">{value}</dd>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

export { Skeleton };
